import { useEffect, useRef, useCallback } from 'react'
import { useAuthStore } from '../store/authStore'
import { useChatStore } from '../store/chatStore'
import { getWebSocketUrl } from '../lib/api'

export function useZumoWebSocket() {
  const { user } = useAuthStore()
  const { activeConversation, addMessage } = useChatStore()
  const wsRef = useRef<WebSocket | null>(null)

  const connect = useCallback(() => {
    if (!user || !activeConversation?.id) return

    const url = getWebSocketUrl(activeConversation.id)
    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => console.log('🔌 Zumo WS connected')

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        switch (data.type) {
          case 'message':
            if (data.payload && data.payload.sender_id !== user.uid) {
              addMessage(activeConversation.id, {
                id: data.payload.id,
                room_id: activeConversation.id,
                sender_id: data.payload.sender_id,
                username: data.payload.username,
                text: data.payload.text,
                type: data.payload.type || 'text',
                reply_to: data.payload.reply_to_id,
                created_at: data.payload.created_at,
                status: 'delivered',
              })
            }
            break
          case 'typing':
            window.dispatchEvent(new CustomEvent('typing', { detail: data.payload }))
            break
          case 'presence':
            window.dispatchEvent(new CustomEvent('presence', { detail: data.payload }))
            break
          case 'presence_list':
            window.dispatchEvent(new CustomEvent('presence_list', { detail: data }))
            break
        }
      } catch {}
    }

    ws.onclose = () => { console.log('🔌 WS closed, reconnecting...'); setTimeout(connect, 3000) }
  }, [user, activeConversation?.id])

  useEffect(() => { connect(); return () => wsRef.current?.close() }, [connect])

  const sendWS = useCallback((type: string, payload: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, ...payload }))
    }
  }, [])

  return { sendWS }
}
